import React, { useContext, useState, useEffect, useMemo } from 'react';

import { Select, Button, Dropdown } from 'antd';
import Axios from 'axios';
import _ from 'lodash';

import styled from 'styled-components';

import { ListContext } from '@contexts/ListContext';
import { useStateValue } from '@contexts/StateProvider';
import { UserContext } from '@contexts/UserContext';

import { useCustomersList, useModelsList } from '@hooks/API/Contact/UseContactsList';
import { useProjectModels } from '@hooks/API/Project/UseProjectModels';

import { NwButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { Option, OptGroup } = Select;

/*
Receives as props an array of Objects {ContacType, DisplayName} used to compose the options fo the select by merging corresponding cached lists.
agencies/services/clients works on the same cached list "customers" filtering it by subtype
models/scouting works on the same cached list "models" filtering it by subtype
items in exclusions prop (array of IDs), if present, are removed from the final list.
*/

const Container = styled.div`
    display: flex;

    .ant-btn:hover,
    .ant-btn:focus {
        border-color: rgba(0, 0, 0, 0.25) !important;
    }

    .ant-btn:hover {
        color: ${StyleVariables.DangerColor} !important;
    }

    .ant-select-selection-item {
        text-transform: capitalize;
    }
`;

const ClearButtonStyle = {
    padding: '0 8px',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    borderRadius: '0 4px 4px 0',
    color: 'rgba(0,0,0,0.25)',
    borderLeft: 'none',
};

const OptionButtonStyle = {
    ...ClearButtonStyle,
    padding: '0 6px',
    borderRadius: 0,
};

const StyledSelect = styled(Select)`
    width: 100%;
    .ant-select-selection {
        border-radius: 4px 0 0 4px;
    }

    .ant-select-selector {
        border-radius: 6px 0 0 6px !important;
    }

    .ant-select-item-option {
        .ant-select-item-option-content {
            text-transform: capitalize;
        }
        &.archived {
            .ant-select-item-option-content {
                color: ${StyleVariables.DangerColor};
            }
        }
    }
`;

const normalizeString = (str) => {
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
        .toLowerCase();
};

// the children of an Option can be an array of strings (the fallback option): reduce them to text
const getOptionText = (children) => {
    return Array.isArray(children) ? children.join('') : String(children);
};

const contactTypes = [
    { code: 'mod', name: 'Models', enabled: false, active: false },
    { code: 'sco', name: 'Scouting Models', enabled: false, active: false },
    { code: 'cli', name: 'Clients', enabled: false, active: false },
    { code: 'age', name: 'Agencies', enabled: false, active: false },
    { code: 'srv', name: 'Services', enabled: false, active: false },
];

const ContactTypeContainer = styled.div`
    display: flex;
    align-items: center;
    font-weight: ${(props) => props.active && 'bold'};
    opacity: ${(props) => !props.active && '0.5'};
`;

const IconContainer = styled.div`
    width: 1rem;
`;

const ContactTypeButtons = ({ activeSources, onChange }) => {
    const enabledSources = activeSources.filter((as) => as.enabled);
    const activeOptions = enabledSources.filter((es) => es.active);

    const handleChangeOption = (active, code) => {
        if (activeOptions.length > 1 || active) {
            onChange(active, code);
        }
    };

    return (
        <Dropdown
            placement="bottomRight"
            menu={{
                items:
                    enabledSources && enabledSources.length
                        ? enabledSources.map((es) => {
                              return {
                                  key: es.code,
                                  onClick: () => handleChangeOption(!es.active, es.code),
                                  label: (
                                      <ContactTypeContainer active={es.active}>
                                          <IconContainer>
                                              {es.active && <NwIcon icon={byPrefixAndName.fasl['check']} />}
                                          </IconContainer>
                                          <div>{es.name}</div>
                                      </ContactTypeContainer>
                                  ),
                              };
                          })
                        : [],
            }}
            trigger={['click']}
        >
            <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
                <NwButton icon={byPrefixAndName.fasl['ellipsis']} style={OptionButtonStyle} />
            </div>
        </Dropdown>
    );
};

const ContactDropdown = ({
    activableTypes,
    activeTypes,
    customPlaceholder,
    disabled,
    exclusions,
    hide,
    hideClearButton,
    isExtendedValue,
    name,
    onBlur,
    onChange,
    projectId,
    topListContacts,
    value,
    ...rest
}) => {
    const { currentUser } = useContext(UserContext);
    const { data: models } = useModelsList();
    const { data: customers } = useCustomersList();
    const { data: ProjectModels } = useProjectModels(projectId ? projectId : null);
    //const { data: ProjectCustomers } = useProjectModels(projectId ? projectId : null);
    const [{ areaContext }] = useStateValue();
    const [missingContact, setMissingContact] = useState();
    const normalizedActiveTypes = Array.isArray(activeTypes) ? activeTypes : [];
    const normalizedActivableTypes = Array.isArray(activableTypes) ? activableTypes : [];
    const activeTypesKey = normalizedActiveTypes.join('|');
    const activableTypesKey = normalizedActivableTypes.join('|');

    const { cachedList } = useContext(ListContext);
    const { accGroupId } = currentUser.ActiveContext;
    const currentAccountingGroup = cachedList.accountingGroups.items.find((group) => group.ID === accGroupId);
    const networkAgencies = currentAccountingGroup && currentAccountingGroup.NetworkAgencies;
    const [options, setOptions] = useState([]);

    const allContactIDs = useMemo(() => {
        const allContacts = [];
        if (models) {
            allContacts.push(...models.map((model) => model.ID));
        }
        if (customers) {
            allContacts.push(...customers.map((customer) => customer.ID));
        }
        return allContacts;
    }, [models, customers]);

    // IDs actually present in the rendered options after all active filters (type, department, etc.).
    const renderedOptionIds = useMemo(() => {
        const ids = new Set();
        options.forEach((group) => {
            group.options.forEach((opt) => {
                ids.add(opt.value);
            });
        });
        return ids;
    }, [options]);

    useEffect(() => {
        let cancelled = false;
        const valueId = isExtendedValue ? parseInt(String(value).split('-')[0]) : value;

        const fetchMissingContact = async () => {
            const missingContactData = await Axios.get(`/contacts/simple/${valueId}`);
            if (!cancelled) {
                setMissingContact(missingContactData || undefined);
            }
        };

        if (!value) {
            setMissingContact(undefined);
        } else if (!allContactIDs.includes(valueId)) {
            // Contact is completely absent from the cached lists: fetch as fallback.
            fetchMissingContact();
        } else if (models && !renderedOptionIds.has(valueId)) {
            // Contact exists in cache but is filtered out of rendered options
            // (e.g. department filter): fetch so it can be displayed as a fallback option.
            fetchMissingContact();
        } else {
            // Contact is properly rendered: clear any stale fallback entry.
            setMissingContact(undefined);
        }

        return () => {
            cancelled = true;
        };
    }, [value, allContactIDs, renderedOptionIds, models]);

    const sourcesFromProps = useMemo(() => {
        const newContactTypes = JSON.parse(JSON.stringify(contactTypes));
        normalizedActiveTypes.map((t) => {
            const mycontIndex = contactTypes.findIndex((ct) => ct.code === t);
            if (mycontIndex > -1) {
                newContactTypes[mycontIndex].enabled = true;
                newContactTypes[mycontIndex].active = true;
            }
            return null;
        });
        normalizedActivableTypes.map((t) => {
            const mycontIndex = contactTypes.findIndex((ct) => ct.code === t);
            if (mycontIndex > -1) {
                newContactTypes[mycontIndex].enabled = true;
                newContactTypes[mycontIndex].active = false;
            }
            return null;
        });
        return newContactTypes;
    }, [activeTypesKey, activableTypesKey]);
    const [activeSources, setActiveSources] = useState(sourcesFromProps);

    useEffect(() => {
        setActiveSources(sourcesFromProps);
    }, [sourcesFromProps]);

    const isArchived = (contact) => {
        if (contact.SAG && contact.SAG.length > 0) {
            //archived at least in one of the context network agencies
            if (networkAgencies.some((na) => contact.SAG.includes(na.ID))) {
                //archived in all the context network agencies
                if (networkAgencies.every((na) => contact.SAG.includes(na.ID))) {
                    return 2;
                }
                return 1;
            }
        }
        return 0;
    };

    const getArchivedText = (contact) => {
        const archivedContact = isArchived(contact);
        let archivedText = '';
        if (archivedContact === 2) {
            archivedText += ' (ARCHIVED)';
        } else if (archivedContact === 1) {
            archivedText += ' (ARCHIVED IN ';
            const archivedAgencies = networkAgencies.filter((na) => contact.SAG.includes(na.ID));
            archivedText += archivedAgencies.map((agency) => agency.Name.toUpperCase()).join(', ');
            archivedText += ')';
        }
        return archivedText;
    };

    const getLabelForContact = (contact) => {
        let cname = contact.N.toLowerCase();
        if (contact.S) {
            cname += ' ' + contact.S.toLowerCase();
        }
        if (contact.SAG && contact.SAG.length > 0) {
            cname += getArchivedText(contact);
        }
        return cname;
    };

    const getCodeItems = (code) => {
        let dataList = [];
        let label = 'contacts';
        let excludedContacts = exclusions ? [...exclusions] : [];
        if (topListContacts && topListContacts.length) {
            const topListContactsIds = topListContacts.map((contact) => contact.ID);
            excludedContacts.push(...topListContactsIds);
            excludedContacts = Array.from(new Set(excludedContacts));
        }
        const activeDepartments = currentUser.ActiveContext.activeDepartments;
        switch (code) {
            case 'mod':
                let modelList = models ? [...models] : [];
                if (areaContext.area !== 'accounting' && areaContext.area !== 'management') {
                    modelList = modelList.filter((model) =>
                        model.DEPS.some((departmentId) => activeDepartments.includes(departmentId))
                    );
                }
                dataList = modelList
                    .filter((model) => model.ST === 4 && !excludedContacts.includes(model.ID))
                    .map((model) => ({
                        label: getLabelForContact(model),
                        value: model.ID,
                        archived: isArchived(model),
                    }));
                label = 'models';
                break;
            case 'sco':
                let scoutingModelList = models ? [...models] : [];
                if (areaContext.area !== 'accounting' && areaContext.area !== 'management') {
                    scoutingModelList = scoutingModelList.filter((model) =>
                        model.DEPS.some((departmentId) => activeDepartments.includes(departmentId))
                    );
                }
                dataList = scoutingModelList
                    .filter((model) => model.ST === 5 && !excludedContacts.includes(model.ID))
                    .map((model) => ({
                        label: getLabelForContact(model),
                        value: model.ID,
                        archived: isArchived(model),
                    }));
                label = 'scouting models';
                break;
            case 'cli':
                dataList = customers
                    ? customers
                          .filter((customer) => customer.ST === 1 && !excludedContacts.includes(customer.ID))
                          .map((customer) => ({
                              label: getLabelForContact(customer),
                              value: customer.ID,
                              archived: isArchived(customer),
                          }))
                    : [];
                label = 'clients';
                break;
            case 'age':
                dataList = customers
                    ? customers
                          .filter((customer) => customer.ST === 2 && !excludedContacts.includes(customer.ID))
                          .map((customer) => ({
                              label: getLabelForContact(customer),
                              value: customer.ID,
                              archived: isArchived(customer),
                          }))
                    : [];
                label = 'agencies';
                break;
            case 'srv':
                dataList = customers
                    ? customers
                          .filter((customer) => customer.ST === 3 && !excludedContacts.includes(customer.ID))
                          .map((customer) => ({
                              label: getLabelForContact(customer),
                              value: customer.ID,
                              archived: isArchived(customer),
                          }))
                    : [];
                label = 'services';
                break;
            default:
                dataList = models
                    ? models
                          .filter((model) => model.ST === 4)
                          .map((model) => ({
                              label: getLabelForContact(model),
                              value: model.ID,
                              archived: isArchived(model),
                          }))
                    : [];
                label = 'models';
                break;
        }

        // if (projectId && currentProject) {
        //     const { ProjectModels } = currentProject;
        //     dataList = dataList.filter(item => ProjectModels.findIndex(model => model.ModelID === item.value) >= 0);
        // }

        if (projectId && ProjectModels) {
            dataList = dataList.filter((item) => ProjectModels.findIndex((model) => model.ModelID === item.value) >= 0);
        }
        dataList.sort((a, b) => a.label.localeCompare(b.label));
        return {
            label: label,
            options: [...dataList],
        };
    };

    const mapOptions = () => {
        return activeSources
            .filter((as) => as.active)
            .map((source) => {
                return getCodeItems(source.code.toLowerCase());
            });
    };

    useEffect(() => {
        const topOptions = topListContacts
            ? {
                  label: 'Project contacts',
                  options: topListContacts.map((contact) => ({ label: contact.Name.toLowerCase(), value: contact.ID })),
              }
            : null;
        const listOptions = mapOptions();
        const options = [];
        if (topOptions) {
            options.push(topOptions);
        }
        options.push(...listOptions);
        setOptions(options);
    }, [activeSources, exclusions, models, customers, ProjectModels, topListContacts]);

    const updateSources = (contacttype, checked) => {
        const newActiveSources = _.clone(activeSources);
        const todisable = newActiveSources.find((s) => {
            return s.code === contacttype;
        });
        if (todisable) {
            todisable.active = checked;
        }
        setActiveSources(newActiveSources);
    };

    const handleChangeContactButtons = (checked, contacttype) => {
        if (!checked && value) {
            const { options } = getCodeItems(contacttype);
            if (options.find((option) => (option.value === isExtendedValue ? parseInt(value.split('-')[0]) : value))) {
                onChange(undefined);
            }
        }
        updateSources(contacttype, checked);
    };

    const contactPlaceHolderName = (contactType) => {
        switch (contactType) {
            case 'mod':
                return 'model';
            case 'sco':
                return 'scouting';
            case 'cli':
                return 'client';
            case 'age':
                return 'agency';
            case 'srv':
                return 'service';
            default:
                return null;
        }
    };

    const contactTypePlaceholder = useMemo(() => {
        let placeholder = null;
        if (normalizedActiveTypes.length === 1 && normalizedActivableTypes.length === 0) {
            placeholder = `Select a ${contactPlaceHolderName(normalizedActiveTypes[0])}`;
        }
        return placeholder;
    }, [activeTypesKey, activableTypesKey]);

    return (
        <Container {...rest}>
            <StyledSelect
                autoComplete="off"
                name={name}
                showSearch
                value={value}
                defaultValue={value}
                disabled={disabled}
                placeholder={
                    customPlaceholder
                        ? customPlaceholder
                        : contactTypePlaceholder
                          ? contactTypePlaceholder
                          : 'Select a contact'
                }
                onChange={(value) => onChange(value)}
                onBlur={onBlur}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                filterOption={(input, option) => {
                    // Skip if option has no value or children
                    if (!option || !option.value || !option.children) return false;

                    // Normalize both the input and the option text
                    const normalizedInput = normalizeString(input);
                    const normalizedOption = normalizeString(getOptionText(option.children));

                    return normalizedOption.includes(normalizedInput);
                }}
            >
                {missingContact && (
                    <Option key={missingContact.ID} value={missingContact.ID}>
                        {missingContact.FullName} {missingContact.StorageBooking ? ' (ARCHIVED)' : ''}
                    </Option>
                )}
                {options.map((group) => (
                    <OptGroup key={group.label} label={group.label}>
                        {group.options.map((opt) => (
                            <Option
                                className={opt.archived ? 'archived' : ''}
                                key={opt.value}
                                value={isExtendedValue ? `${opt.value}-${group.label}` : opt.value}
                            >
                                {opt.label}
                            </Option>
                        ))}
                    </OptGroup>
                ))}
            </StyledSelect>
            <Button.Group style={{ display: 'flex', borderRadius: 0 }}>
                {!contactTypePlaceholder && !hide && (
                    <ContactTypeButtons activeSources={activeSources} onChange={handleChangeContactButtons} />
                )}
                {!hideClearButton && (
                    <NwButton
                        icon={byPrefixAndName.fasl['times']}
                        style={ClearButtonStyle}
                        onClick={(event) => onChange(undefined)}
                    />
                )}
            </Button.Group>
        </Container>
    );
};

export default ContactDropdown;
